import { memo } from 'react'

import { BASE_URL2 } from 'config/axios'

import LabelCard from '../LabelCard'
import MultiFabricLabels from '../MultiFabricLabels'
import { FrLogo, ZaraQr } from 'assets/icons'
import useOrderStore from 'store/client/orderStore'
import { getScaleForMinGlyphMm } from '../labelGlyphScale'

const getCardType = (number) =>
  number % 2 === 0 ? 'back' : 'front'

const Page2 = ({ data }) => {
  const multiFabricResult =
    MultiFabricLabels({ data }) || {}

  const {
    cards = [],
    lastNumber = 5,
  } = multiFabricResult

  const parsedLastNumber = Number(lastNumber)

  /*
   * MultiFabric son kullanılan numaradan sonraki
   * numarayı lastNumber olarak dönmeli.
   */
  let currentNumber = Number.isFinite(parsedLastNumber)
    ? parsedLastNumber
    : 5

  const { referance } = useOrderStore()

  const isBlackLabel =
    referance?.backgroundColor === 'B'

  const symbol =
    data?.washingLabel?.[0]?.symbol ?? {}

  const additionalPhrases = Array.isArray(
    data?.additionalPhrases
  )
    ? data.additionalPhrases
    : []

  const washingAdditionalPhrases = Array.isArray(
    data?.washingAdditionalPhrases
  )
    ? data.washingAdditionalPhrases
    : []

  const symbolTranslations = Array.isArray(
    data?.symbolTranslate
  )
    ? data.symbolTranslate
    : []

  const hasSymbolTranslate =
    symbolTranslations.length > 0

  const countryText = Array.isArray(
    data?.country?.translate
  )
    ? data.country.translate
        .map((translate) => translate?.translate)
        .filter(Boolean)
        .join(' - ')
    : ''

  const countryScaleY = getScaleForMinGlyphMm(
    countryText,
    {
      font: '4.7pt Arial',
      minMm: 2,
      baseScale: 1.59,
      maxScale: 4,
    }
  )

  const symbolFolder = isBlackLabel
    ? 'symbolsWhite'
    : 'symbols'

  const symbolItems = [
    // Üst sıra
    {
      group: 'wash',
      suffix: '',
      width: 16,
      height: 16,
    },
    {
      group: 'bleach',
      suffix: '',
      width: 16,
      height: 16,
    },
    {
      group: 'tumble',
      suffix: '',
      width: 16,
      height: 16,
    },
    {
      group: 'iron',
      suffix: '',
      width: 16,
      height: 16,
    },
    {
      group: 'dry',
      suffix: '',
      width: 16,
      height: 16,
    },

    // Orta sıra
    {
      group: 'wash',
      suffix: '_1',
      width: 14,
      height: 14,
    },
    {
      group: 'bleach',
      suffix: '_1',
      width: 16,
      height: 16,
    },
    {
      group: 'iron',
      suffix: '_1',
      width: 16,
      height: 16,
    },
    {
      group: 'dry',
      suffix: '_1',
      width: 14,
      height: 14,
    },
    {
      group: 'tumble',
      suffix: '_1',
      width: 16,
      height: 16,
    },

    // Alt sıra
    {
      group: 'wash',
      suffix: '_2',
      width: 16,
      height: 16,
      marginTop: 1,
    },
    {
      group: 'bleach',
      suffix: '_2',
      width: 16,
      height: 16,
    },
    {
      group: 'tumble',
      suffix: '_2',
      width: 16,
      height: 16,
    },
    {
      group: 'iron',
      suffix: '_2',
      width: 16,
      height: 16,
    },
    {
      group: 'dry',
      suffix: '_2',
      width: 16,
      height: 16,
    },
  ]

  /*
   * Additional phrases
   */
  const additionalPhraseCards =
    additionalPhrases.map(
      (translation, index) => {
        const number = currentNumber++
        const text = translation?.text ?? ''
        const longText = text.length > 400

        const font = longText
          ? '4.4pt Arial'
          : '4.7pt Arial'

        const dynamicScaleY =
          getScaleForMinGlyphMm(text, {
            font,
            minMm: 2,
            baseScale: 1.59,
          })

        return (
          <LabelCard
            key={`additional-${number}-${index}`}
            type={getCardType(number)}
            number={number}
          >
            <div className="flex items-center h-full">
              <div
                className={`flex flex-col px-[2.5mm] ${
                  longText
                    ? 'text-[4.4pt] leading-[4.4pt]'
                    : 'text-[4.7pt] leading-[4.7pt]'
                } font-arial text-justify`}
                style={{
                  transform: `scaleY(${dynamicScaleY})`,
                }}
              >
                {text}
              </div>
            </div>
          </LabelCard>
        )
      }
    )

  /*
   * Washing additional phrases
   */
  const washingPhraseCards =
    washingAdditionalPhrases.map(
      (translation, index) => {
        const number = currentNumber++
        const text = translation?.text ?? ''
        const longText = text.length > 400

        const font = longText
          ? '3.8pt Arial'
          : '5.04pt Arial'

        const dynamicScaleY =
          getScaleForMinGlyphMm(text, {
            font,
            minMm: 2,
            baseScale: 1.59,
          })

        return (
          <LabelCard
            key={`washing-${number}-${index}`}
            type={getCardType(number)}
            number={number}
          >
            <div className="flex items-center h-full">
              <div
                className={`flex flex-col px-[2.5mm] ${
                  longText
                    ? 'text-[3.8pt] leading-[3.8pt]'
                    : 'text-[5.04pt] leading-[5.05pt]'
                } tracking-[0.12mm] font-arial text-justify`}
                style={{
                  transform: `scaleY(${dynamicScaleY})`,
                }}
              >
                {text}
              </div>
            </div>
          </LabelCard>
        )
      }
    )

  /*
   * Logo, symbol translate ve QR kartları
   */
  const finalCards = []

  const addEmptyCard = () => {
    const number = currentNumber++

    finalCards.push(
      <LabelCard
        key={`empty-${number}`}
        type={getCardType(number)}
        number={number}
      />
    )
  }

  /*
   * Sıradaki kart istenen tipte değilse
   * araya boş kart ekleyerek hizalar.
   */
  const alignNextCard = (desiredType) => {
    if (
      getCardType(currentNumber) !== desiredType
    ) {
      addEmptyCard()
    }
  }

  const isBrandOrderNo25 =
    typeof data?.brandOrderNo === 'string' &&
    data.brandOrderNo.trim().endsWith('-25')

  if (hasSymbolTranslate) {
    /*
     * brandOrderNo -25 ile bitiyorsa symbolTranslate empty'den önce gelsin.
     * Diğer durumlarda back tarafına hizala (gerekirse empty ekle).
     */
    if (!isBrandOrderNo25) {
      alignNextCard('back')
    }

    const number = currentNumber++

    finalCards.push(
      <LabelCard
        key={`symbol-translate-${number}`}
        type={getCardType(number)}
        number={number}
      >
        <div className="px-[2.5mm] flex flex-col justify-center h-full text-[3.8pt] leading-[4.4pt] scale-y-[1.3]">
          {symbolTranslations.map(
            (item, index) => (
              <div
                key={`symbol-translation-${index}`}
              >
                {item}
              </div>
            )
          )}
        </div>
      </LabelCard>
    )

    if (isBrandOrderNo25) {
      alignNextCard('front')
    }
  } else {
    /*
     * Symbol translate yoksa logo front kartına gelsin.
     */
    alignNextCard('front')

    const number = currentNumber++

    finalCards.push(
      <LabelCard
        key={`logo-${number}`}
        type={getCardType(number)}
        number={number}
      >
        <div className="flex items-center h-full">
          <div className="pl-[13mm] pr-[12mm] pb-[0.5mm] w-full">
            <FrLogo
              className={
                isBlackLabel
                  ? 'fill-white'
                  : 'fill-[#1d1d1b]'
              }
            />
          </div>
        </div>
      </LabelCard>
    )
  }

  /*
   * QR bir önceki kartın devamı olarak yerleşir:
   *
   * Logo front ise QR back olur.
   * Symbol translate back ise QR front olur.
   */
  const qrNumber = currentNumber++

  finalCards.push(
    <LabelCard
      key={`qr-${qrNumber}`}
      type={getCardType(qrNumber)}
      number={qrNumber}
    >
      <div className="flex items-center h-full">
        <div className="flex items-start justify-between px-[6mm] w-full">
          <div className="w-[21.5mm] h-[14mm] grid grid-cols-5 place-items-center items-start gap-y-1">
            {symbolItems.map((item, index) => {
              const symbolName =
                symbol?.[item.group]

              return (
                <img
                  key={`${item.group}-${item.suffix}-${index}`}
                  src={`${BASE_URL2}/images/${symbolFolder}/${item.group}/${symbolName}${item.suffix}.svg`}
                  alt={`${item.group} sembol icon`}
                  style={{
                    width: item.width,
                    height: item.height,
                    padding: 0.4,
                    marginTop:
                      item.marginTop ?? 0,
                  }}
                />
              )
            })}
          </div>

          <div
            className={
              isBlackLabel
                ? 'h-[13.5mm] w-[13.5mm]'
                : 'h-[14.6mm] w-[14.6mm]'
            }
          >
            <ZaraQr
              className={`fill-black bg-white ${
                isBlackLabel
                  ? 'p-[0.1mm]'
                  : ''
              }`}
            />
          </div>
        </div>
      </div>
    </LabelCard>
  )

  return (
    <div className="flex flex-wrap gap-y-3 gap-x-5">
      <LabelCard
        type={getCardType(3)}
        number={3}
      >
        <div className="flex items-center h-full">
          <div className="scale-y-[153%] px-[2.5mm] leading-[4.7pt] text-[4.3pt]">
            <p>
              아이티엑스코리아유한회사
              자라리테일코리아주식회사 서울시
              강남구 삼성 동 무역센터 20층
              080-479-0880 MA-MAROC: Sté
              AZZURRO IF:1641920
              （株）ITXジャパン (0120-713-777)
              IMPORTADO POR ZARA MEXICO, S.A.
              DE C.V. PONIENTE 146 NO. 730
              COLONIA INDUSTRIAL VALLEJO
              ALCALDIA AZCAPOTZALCO, C.P.
              02300, CIUDAD DE MEXICO,
              RFC:ZME920824KM3
            </p>
          </div>
        </div>
      </LabelCard>

      <LabelCard
        type={getCardType(4)}
        number={4}
      >
        <div className="flex items-center h-full">
          <p
            className="px-[2.5mm] leading-[4.7pt] text-justify"
            style={{
              transform: `scaleY(${countryScaleY})`,
            }}
          >
            {countryText}
          </p>
        </div>
      </LabelCard>

      {cards}

      {additionalPhraseCards}

      {washingPhraseCards}

      {finalCards}
    </div>
  )
}

export default memo(Page2)